<script setup>
/**
 * SzContactSidebarStandalone — réplica do ContactPanel do chat,
 * porém sem dependência de conversa selecionada. Usado na página /contacts/:id.
 *
 * Estrutura:
 *  - Header "Contato"
 *  - ContactInfo (avatar + nome + ações)
 *  - Tabs Detalhes / Histórico (N) — sempre visível
 *  - Modo Detalhes: ContactInfo body (phone/email/endereço/redes) + Notas + Produtos SZ
 *  - Modo Histórico: ContactPanelHistory
 */
import { computed, watch, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import ContactInfo from 'dashboard/routes/dashboard/conversation/contact/ContactInfo.vue';
import ContactNotes from 'dashboard/routes/dashboard/conversation/contact/ContactNotes.vue';
import ContactPanelHistory from 'dashboard/routes/dashboard/conversation/ContactPanelHistory.vue';
import ConversationAction from 'dashboard/routes/dashboard/conversation/ConversationAction.vue';
import ConversationParticipant from 'dashboard/routes/dashboard/conversation/ConversationParticipant.vue';
import ConversationInfo from 'dashboard/routes/dashboard/conversation/ConversationInfo.vue';
import SzScheduledMessages from 'dashboard/components/widgets/conversation/solarz/SzScheduledMessages.vue';
import SzProductsPanel from 'dashboard/components/widgets/conversation/solarz/SzProductsPanel.vue';

const TAB_STORAGE_KEY = 'sz_contact_standalone_tab';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const store = useStore();
const contactGetter = useMapGetter('contacts/getContact');
const contact = computed(() => contactGetter.value(props.contactId) || {});

// Tenta extrair channel_type do primeiro contact_inbox WhatsApp pra renderizar ícone.
const channelType = computed(() => {
  const ci = contact.value.contactInboxes || contact.value.contact_inboxes || [];
  if (!ci.length) return null;
  return ci[0]?.inbox?.channel_type || ci[0]?.inbox?.channelType || null;
});

// Tab state
const initialTab = (() => {
  try {
    const stored = localStorage.getItem(TAB_STORAGE_KEY);
    return stored === 'history' ? 'history' : 'details';
  } catch (e) { return 'details'; }
})();
const activeTab = ref(initialTab);

function setTab(tab) {
  if (tab === activeTab.value) return;
  activeTab.value = tab;
  try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch (e) {}
  if (tab === 'history' && props.contactId) {
    store.dispatch('contactConversations/get', props.contactId);
  }
}

// Histórico - count
const contactConversationsGetter = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const conversationsList = computed(() => contactConversationsGetter.value(props.contactId) || []);
const historyCount = computed(() => conversationsList.value.length);

// SZ: usa a 1a conversa (mais recente) pra alimentar sections que precisam de conversationId.
const latestConversation = computed(() => conversationsList.value[0] || null);
const latestConversationId = computed(() => latestConversation.value?.id || null);
const latestInboxId = computed(() => latestConversation.value?.inbox_id || null);

const conversationMetadataGetter = useMapGetter('conversationMetadata/getConversationMetadata');
const conversationAdditionalAttributes = computed(() => {
  const id = latestConversationId.value;
  if (!id) return {};
  const meta = conversationMetadataGetter.value(id) || {};
  return meta.additional_attributes || {};
});
const contactAdditionalAttributes = computed(() => contact.value.additional_attributes || {});

function fetchContactDetails() {
  if (props.contactId) {
    store.dispatch('contacts/show', { id: props.contactId });
    store.dispatch('contactConversations/get', props.contactId);
    store.dispatch('contactNotes/get', { contactId: props.contactId });
  }
}

watch(() => props.contactId, (newId, oldId) => {
  if (newId && newId !== oldId) fetchContactDetails();
});

onMounted(() => fetchContactDetails());
</script>

<template>
  <div class="w-full">
    <!-- Header "Contato" -->
    <div class="sz-contact-header flex items-center justify-between px-4 py-2 border-b border-n-weak h-14">
      <span class="font-medium text-sm text-n-slate-12">Contato</span>
    </div>

    <!-- Avatar + nome do contato -->
    <ContactInfo :contact="contact" :channel-type="channelType" header-only hide-actions />

    <!-- Tabs Detalhes / Histórico -->
    <div class="px-4 pt-3 pb-2 border-t border-n-weak">
      <div
        class="relative flex p-1 rounded-lg bg-n-alpha-1 sz-tab-wrap"
        :class="`sz-tab-wrap--${activeTab}`"
      >
        <span class="sz-tab-pill bg-n-solid-1 shadow-sm" aria-hidden="true"></span>
        <button
          type="button"
          class="relative z-10 flex-1 px-3 py-1.5 text-sm font-medium transition-colors"
          :class="activeTab === 'details' ? 'text-n-slate-12' : 'text-n-slate-11 hover:text-n-slate-12'"
          @click="setTab('details')"
        >Detalhes</button>
        <button
          type="button"
          class="relative z-10 flex-1 px-3 py-1.5 text-sm font-medium transition-colors"
          :class="activeTab === 'history' ? 'text-n-slate-12' : 'text-n-slate-11 hover:text-n-slate-12'"
          @click="setTab('history')"
        >Histórico<span v-if="historyCount" class="ml-1 opacity-70">({{ historyCount }})</span></button>
      </div>
    </div>

    <!-- Detalhes -->
    <div v-show="activeTab === 'details'">
      <ContactInfo :contact="contact" :channel-type="channelType" body-only :show-avatar="false" />

      <!-- Sections que dependem de conversation (usa a 1a/mais recente do contato) -->
      <template v-if="latestConversationId">
        <ConversationAction :conversation-id="latestConversationId" :inbox-id="latestInboxId" />
        <ConversationParticipant :conversation-id="latestConversationId" :inbox-id="latestInboxId" />
        <SzScheduledMessages :conversation-id="latestConversationId" />
        <div class="border-t border-n-weak">
          <h4 class="text-sm font-medium text-n-slate-12 m-0 px-4 pt-3 pb-1">Informações da conversa</h4>
          <div class="px-4 pb-3">
            <ConversationInfo
              :conversation-attributes="conversationAdditionalAttributes"
              :contact-attributes="contactAdditionalAttributes"
            />
          </div>
        </div>
      </template>

      <!-- Notas -->
      <div class="border-t border-n-weak pb-8">
        <ContactNotes v-if="contactId" :contact-id="contactId" />
      </div>

      <!-- Produtos SolarZ (SZ CRM / Monitoramento / Pay) -->
      <SzProductsPanel />
    </div>

    <!-- Histórico -->
    <div v-show="activeTab === 'history'">
      <ContactPanelHistory
        v-if="contactId"
        :contact-id="contactId"
        :current-conversation-id="null"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
// Sliding pill nas tabs (mesma técnica do ContactPanel)
.sz-tab-wrap { isolation: isolate; }
.sz-tab-pill {
  position: absolute;
  top: 0.25rem; bottom: 0.25rem; left: 0.25rem;
  width: calc(50% - 0.25rem);
  border-radius: 0.375rem;
  transition: transform 240ms cubic-bezier(0.32, 0.72, 0, 1);
  pointer-events: none;
  z-index: 0;
}
.sz-tab-wrap--history .sz-tab-pill { transform: translateX(100%); }
</style>
